import { useSocket } from "common/hooks/useSocket";
import React, { useContext, useEffect, useState } from "react";
import { DraftContext } from "../context";
import { Button, CardMedia, Paper, Stack } from "@mui/material";
import FilteringBar from "./FilteringBar";
import { DraftPoolEntry, RoomContext } from "App/context";
import { DraftCharacter } from "types/Draft/type";
import CharacterBadges from "../CharacterBadges";
import { FilterContext, FilterProvider } from "./context";
import { BalancingBansContext } from "pages/WaitingRoom/BalancingBans/context";
import { JokerBansContext } from "pages/WaitingRoom/JokerBans/context";
import { API_URL } from "service/root/rootApi";
import { Role } from "types/Room/type";
import { isCaptainRole, useActingRole } from "common/hooks/useActingRole";
import { motion, AnimatePresence } from "motion/react";
import { CharacterSheet, Sheet } from "service/sheet/type";
import { getSheet } from "service/sheet";

const BoxList = () => {
  const socket = useSocket();
  const {
    currentStep,
    selectedPicksPlayer1,
    selectedPicksPlayer2,
    selectedBansPlayer1,
    selectedBansPlayer2,
  } = useContext(DraftContext);
  const { element, rarity, displayMode, player, search, sortConstelDesc } =
    useContext(FilterContext);
  const {
    params,
    role,
    jokerBansPlayer1,
    jokerBansPlayer2,
    charactersList,
    draftPool,
  } = useContext(RoomContext);
  const {
    selectingBans: selectingBalancingBans,
    selectedBansPlayer1: selectedBalancingBansPlayer1,
    selectedBansPlayer2: selectedBalancingBansPlayer2,
  } = useContext(BalancingBansContext);
  const {
    selectingBans: selectingJokerBans,
    selectedBansPlayer1: selectedJokerBansPlayer1,
    selectedBansPlayer2: selectedJokerBansPlayer2,
  } = useContext(JokerBansContext);
  const [sheet, setSheet] = useState<Sheet>();
  const draftActingRole = useActingRole();
  // En solo, le joueur agit pour le camp qui joue : celui de l'étape en draft, celui qui pose
  // les bans pré-draft en waiting room.
  const actingRole: Role =
    params.solo && isCaptainRole(role) && currentStep === undefined
      ? (selectingBalancingBans ?? selectingJokerBans ?? role)
      : draftActingRole;

  const isDraftTurn =
    isCaptainRole(actingRole) &&
    (currentStep?.player === actingRole || currentStep?.player === "Both");
  const draftSelection: DraftCharacter[] | undefined =
    isDraftTurn && currentStep?.action === "Pick"
      ? actingRole === "Player 1"
        ? selectedPicksPlayer1
        : selectedPicksPlayer2
      : isDraftTurn && currentStep?.action === "Ban"
        ? actingRole === "Player 1"
          ? selectedBansPlayer1
          : selectedBansPlayer2
        : undefined;
  const currentSelect: DraftCharacter[] | undefined =
    currentStep !== undefined
      ? draftSelection
      : actingRole === "Player 1"
        ? selectedBalancingBansPlayer1 ?? selectedJokerBansPlayer1
        : actingRole === "Player 2"
          ? selectedBalancingBansPlayer2 ?? selectedJokerBansPlayer2
          : undefined;
  const myTurn =
    isDraftTurn ||
    (currentStep === undefined &&
      (selectingJokerBans === actingRole ||
        selectingBalancingBans === actingRole));

  /*
    Le pool vient du serveur : il a déjà appliqué les règles du jeu (bans d'équilibrage, bans
    joker, bans et picks des deux camps, possession). On n'applique plus ici que les filtres
    d'affichage de la barre, en allant chercher élément et rareté dans le catalogue.
  */
  const getCharactersList = (): DraftPoolEntry[] =>
    draftPool.filter((character) => {
      if (search !== "")
        return character.name.toLowerCase().includes(search.toLowerCase());

      const info = charactersList.find((c) => c.name === character.name);
      const charElement = info?.element;
      const matchesElement =
        element !== null
          ? Array.isArray(charElement)
            ? charElement.includes(element)
            : charElement === element
          : true;
      const matchesRarity = rarity !== null ? info?.rarity === rarity : true;
      return matchesElement && matchesRarity;
    });

  const isCharacterSelected = (character: DraftCharacter) => {
    return currentSelect?.some((ch) => ch.name === character.name);
  };

  const onClickCharacter = (character: DraftPoolEntry) => {
    // Le serveur refuserait l'action de toute façon : on n'envoie pas une sélection illégale.
    if (myTurn && character.selectable && currentSelect !== undefined) {
      const nextSelection = [...currentSelect];
      const index = nextSelection.findIndex(
        (ch) => ch.name === character.name,
      );
      if (index !== -1) {
        nextSelection.splice(index, 1);
      } else {
        // Le plafond ne vaut qu'en draft : les bans d'équilibrage / joker n'ont pas de limite.
        if (currentStep && nextSelection.length >= currentStep.quantity) {
          nextSelection.pop();
        }
        nextSelection.push(character);
      }
      socket.emit(
        currentStep
          ? currentStep.action === "Pick"
            ? "send-selected-chars-pick"
            : "send-selected-chars-ban"
          : selectingBalancingBans
            ? "send-selected-balanced-ban"
            : selectingJokerBans
              ? "send-selected-joker-ban"
              : "",
        nextSelection,
        // Camp joué, lu par le serveur en solo pour les bans pré-draft.
        actingRole,
      );
    }
  };

  /*
    Seul filtre restant côté client : le sélecteur de joueur de la barre d'outils. Toutes les
    règles du jeu qui vivaient ici sont appliquées par le serveur avant l'envoi du pool.
  */
  const applyFilters = (character: DraftPoolEntry) => {
    if (player !== null) {
      if (player === "Player 1") return character.p1Constellation !== undefined;
      else return character.p2Constellation !== undefined;
    }
    return true;
  };

  const getConstellationValue = (
    constellation?: number,
    points?: CharacterSheet,
  ) => {
    if (displayMode === "Constellation") return constellation;
    if (points === undefined) return 0;
    switch (constellation) {
      case 0:
        return points.c0;
      case 1:
        return points.c1;
      case 2:
        return points.c2;
      case 3:
        return points.c3;
      case 4:
        return points.c4;
      case 5:
        return points.c5;
      case 6:
        return points.c6;
    }
  };

  const applySorting = (a: DraftCharacter, b: DraftCharacter) => {
    const getCount = (constel?: number) => {
      if (constel === undefined) return -1;
      if (constel > 0) {
        return constel ** constel;
      }
      return 0;
    };
    const characterPointsA = sheet?.characters.find(
      (c) => c.character.name === a.name,
    );
    const characterPointsB = sheet?.characters.find(
      (c) => c.character.name === b.name,
    );
    if (sortConstelDesc) {
      if (player === "Player 1")
        return (
          (getConstellationValue(b.p1Constellation, characterPointsB) ?? 0) -
          (getConstellationValue(a.p1Constellation, characterPointsA) ?? 0)
        );
      if (player === "Player 2")
        return (
          (getConstellationValue(b.p2Constellation, characterPointsB) ?? 0) -
          (getConstellationValue(a.p2Constellation, characterPointsA) ?? 0)
        );
      const aCount =
        getCount(getConstellationValue(a.p1Constellation, characterPointsA)) +
        getCount(getConstellationValue(a.p2Constellation, characterPointsA));
      const bCount =
        getCount(getConstellationValue(b.p1Constellation, characterPointsB)) +
        getCount(getConstellationValue(b.p2Constellation, characterPointsB));
      return bCount - aCount;
    }
    return 0;
  };

  const renderConstellation = (
    character: DraftCharacter,
    side: "player1" | "player2",
  ) => {
    const characterPoints = sheet?.characters.find(
      (c) => c.character.name === character.name,
    );
    return (
      <CharacterBadges
        character={character}
        side={side}
        format={(constellation) =>
          getConstellationValue(constellation, characterPoints)
        }
      />
    );
  };

  useEffect(() => {
    if (params.sheet.id) {
      const fetch = async () => {
        setSheet(await getSheet(params.sheet.id.toString()));
      };
      fetch();
    }
  }, []);

  return (
    <>
      <FilteringBar />
      <Paper
        sx={{
          overflow: "auto",
          height: "100%",
          width: "100%",
          flexGrow: "1",
          bgcolor: "transparent",
          overflowX: "hidden",
        }}
      >
        <Stack
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(10rem, 1fr))",
            gap: 0.75,
            padding: 0.75,
          }}
        >
          <AnimatePresence>
            {getCharactersList()
              .filter((character) => applyFilters(character))
              .sort((a, b) => applySorting(a, b))
              .map((character) => (
                <motion.div
                  key={character.name}
                  initial={{ scale: 0 }}
                  animate={{
                    scale: 1,
                    transition: { duration: 0.1 },
                  }}
                  exit={{
                    opacity: 0,
                    transition: { duration: 0.1 },
                  }}
                  whileHover={{ scale: 1.13 }}
                  layout
                >
                  <Button
                    onClick={() => onClickCharacter(character)}
                    disabled={myTurn && !character.selectable}
                    sx={{
                      height: "9rem",
                      filter: `grayscale(${
                        myTurn && !character.selectable ? 1 : 0
                      })`,
                      backgroundColor: ({ palette }) =>
                        isCharacterSelected(character)
                          ? currentStep?.action === "Pick"
                            ? palette.common.player1.banCard
                            : palette.common.player2.banCard
                          : "transparent",
                      ":hover": {
                        backgroundColor: ({ palette }) =>
                          isCharacterSelected(character)
                            ? currentStep?.action === "Pick"
                              ? palette.common.player1.banCard
                              : palette.common.player2.banCard
                            : "transparent",
                      },
                    }}
                  >
                    <Stack
                      sx={{
                        height: "100%",
                        width: "100%",
                        position: "relative",
                        border: ({ palette }) =>
                          `1px solid ${palette.common.grey.g474a53}`,
                        overflow: "hidden",
                        borderRadius: "0.5rem",
                      }}
                    >
                      {character.p1Constellation !== undefined &&
                        !jokerBansPlayer2?.some(
                          (c) => c.name === character.name,
                        ) &&
                        renderConstellation(character, "player1")}
                      {character.p2Constellation !== undefined &&
                        !jokerBansPlayer1?.some(
                          (c) => c.name === character.name,
                        ) &&
                        renderConstellation(character, "player2")}
                      <CardMedia
                        component="img"
                        title={character.name}
                        src={`${API_URL}assets/genshin/characters/${character.name}/profile.png`}
                        sx={{
                          objectPosition: "75% 25%",
                          bgcolor: ({ palette }) =>
                            `${palette.background.paper}99`,
                          transition: "transform .1s",
                        }}
                      />
                    </Stack>
                  </Button>
                </motion.div>
              ))}
          </AnimatePresence>
        </Stack>
      </Paper>
    </>
  );
};

const BoxListWithContext = () => {
  return (
    <FilterProvider>
      <BoxList />
    </FilterProvider>
  );
};

export default BoxListWithContext;
